/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import type Mithril from 'mithril';

/**
 * The content of a single tour step, as built by `MobileTourCard`.
 */
export type MobileTourStepData = {
	id: string;
	image: string;
	title: string;
	textShort: string;
	textFull: string;
};

export interface MobileTourStepModalAttrs extends IInternalModalAttrs, MobileTourStepData {}

/**
 * Minimal shape of the `body-scroll-lock` UMD global, loaded from a CDN by
 * `loadDynLib` and therefore not typed by any package.
 */
interface BodyScrollLock {
	disableBodyScroll(target: HTMLElement): void;
	clearAllBodyScrollLocks(): void;
}

declare const bodyScrollLock: BodyScrollLock | undefined;

export default class MobileTourStepModal<CustomAttrs extends MobileTourStepModalAttrs = MobileTourStepModalAttrs> extends Modal<CustomAttrs> {
	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		// To be able to scroll inside of this modal we need to disable body scroll lock
		// (the lock had been activated in the MobileTour component)
		bodyScrollLock?.clearAllBodyScrollLocks();
	}

	onremove(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
		super.onremove(vnode);

		bodyScrollLock?.disableBodyScroll(document.body);
	}

	className(): string {
		return 'Modal--small MobileTourStepModal';
	}

	title(): Mithril.Children {
		const { title } = this.attrs;
		return m.trust(title);
	}

	content(): Mithril.Children {
		const { image, textFull } = this.attrs;
		return (
			<div className="Modal-body">
				<div className="Form">
					<div className="Form-group">
						<div className="MobileTourStepModal-image" style={{ backgroundImage: `url(${image})` }}></div>
						<p className="MobileTourStepModal-text">{m.trust(textFull)}</p>
					</div>
				</div>
			</div>
		);
	}
}
